/**
 * Local Config Override Utility
 *
 * This utility allows developers to override/extend configurations fetched from
 * Secrets Manager with local config.json values during development.
 *
 * HOW IT WORKS:
 * - In production: Only Secrets Manager configs are used (no override)
 * - In development (localhost):
 *   1. Fetches config from Secrets Manager (using mapped config key)
 *   2. Fetches local config.json (using project folder name)
 *   3. Deep merges: secretsConfig + localConfig (local wins on conflict)
 *
 * NAMING CONVENTION:
 * - projectName: The actual folder name (e.g., 'c3-linear-monitoring')
 *   → Used for local config path: /c3-linear-monitoring/config/config.json
 * - configKey: Derived from projects-map.ts (e.g., 'c3')
 *   → Used for Secrets Manager lookup
 *
 * USAGE:
 * - Add/modify values in your app's config/config.json
 *   (Located at: apps/{project-name}/config/config.json)
 * - These values will override matching keys from Secrets Manager
 * - New keys will be added to the final config
 *
 * EXAMPLE:
 * Secrets Manager (vcap-configs-c3):  { api: { url: "https://prod.api.com" }, feature: true }
 * Local (/c3-linear-monitoring/config/config.json): { api: { url: "http://localhost:3000" }, debug: true }
 * Final merged config:  { api: { url: "http://localhost:3000" }, feature: true, debug: true }
 */
/**
 * Deep merge two objects. Source values override target values.
 * Nested objects are merged recursively instead of being overwritten.
 */
function deepMerge(target, source) {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            if (target[key] !== null && typeof target[key] === 'object' && !Array.isArray(target[key])) {
                result[key] = deepMerge(target[key], source[key]);
            }
            else {
                result[key] = { ...source[key] };
            }
        }
        else {
            result[key] = source[key];
        }
    }
    return result;
}
/**
 * Logs detailed override information for debugging.
 * Shows which keys were overridden with before/after values.
 */
function logOverrideDetails(secretsConfig, localConfig, prefix = '') {
    for (const key of Object.keys(localConfig)) {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const localValue = localConfig[key];
        const secretsValue = secretsConfig?.[key];
        // Check if it's a nested object (not array)
        if (localValue !== null && typeof localValue === 'object' && !Array.isArray(localValue)) {
            if (secretsValue !== null && typeof secretsValue === 'object' && !Array.isArray(secretsValue)) {
                // Recursively log nested overrides
                logOverrideDetails(secretsValue, localValue, fullKey);
            }
            else {
                // New object added
                console.log(`🔧 [DEV] ➕ NEW: "${fullKey}" added from local config`);
                console.log(`   Local:   ${JSON.stringify(localValue)}`);
            }
        }
        else {
            // Leaf value - check if override or new
            if (secretsValue !== undefined) {
                // Value exists in secrets - this is an override
                if (JSON.stringify(secretsValue) !== JSON.stringify(localValue)) {
                    console.log(`🔧 [DEV] 🔄 OVERRIDE: "${fullKey}"`);
                    console.log(`   Secrets: ${JSON.stringify(secretsValue)}`);
                    console.log(`   Local:   ${JSON.stringify(localValue)} ✅`);
                }
            }
            else {
                // Value doesn't exist in secrets - this is a new key
                console.log(`🔧 [DEV] ➕ NEW: "${fullKey}" = ${JSON.stringify(localValue)}`);
            }
        }
    }
}
/**
 * Checks if the application is running in development mode (localhost).
 */
export function isDevelopmentMode() {
    return window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';
}
/**
 * Fetches local config.json file if it exists.
 * Returns null if file doesn't exist or fetch fails.
 *
 * @param projectName - The project folder name (e.g., 'c3-linear-monitoring')
 *                      Used to construct path: /{projectName}/config/config.json
 */
export async function fetchLocalConfig(projectName) {
    // Only attempt to fetch local config in development mode
    if (!isDevelopmentMode()) {
        return null;
    }
    // Construct config path using project folder name
    // e.g., /c3-linear-monitoring/config/config.json
    const path = `/${projectName}/config/config.json`;
    try {
        console.log(`🔧 [DEV] Attempting to fetch local config from: ${path}`);
        const response = await fetch(path, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (!response.ok) {
            if (response.status === 404) {
                console.log('🔧 [DEV] No local config.json found (this is OK)');
                return null;
            }
            console.warn(`🔧 [DEV] Failed to fetch local config: ${response.status}`);
            return null;
        }
        const localConfig = await response.json();
        console.log('🔧 [DEV] Local config.json loaded successfully');
        console.log('🔧 [DEV] Local config keys:', Object.keys(localConfig));
        return localConfig;
    }
    catch (error) {
        // Silently fail - local config is optional
        console.log('🔧 [DEV] Could not load local config.json (this is OK):', error instanceof Error ? error.message : 'Unknown error');
        return null;
    }
}
/**
 * Merges secrets config with local config (if available in development).
 * Local config values override secrets config values.
 *
 * @param secretsConfig - Configuration fetched from Secrets Manager
 * @param projectName - The project folder name (e.g., 'c3-linear-monitoring')
 *                      Used for local config path: /{projectName}/config/config.json
 * @returns Merged configuration (secrets + local overrides)
 */
export async function mergeWithLocalConfig(secretsConfig, projectName) {
    // In production, return secrets config as-is
    if (!isDevelopmentMode()) {
        return secretsConfig;
    }
    console.log('🔧 [DEV] Development mode detected - checking for local config override...');
    // Try to fetch local config using project folder name
    const localConfig = await fetchLocalConfig(projectName);
    if (!localConfig) {
        console.log('🔧 [DEV] No local overrides - using Secrets Manager config only');
        return secretsConfig;
    }
    // Log detailed override information
    console.log('🔧 [DEV] ────────────────────────────────────────────');
    console.log('🔧 [DEV] 📝 Override Details:');
    logOverrideDetails(secretsConfig, localConfig);
    console.log('🔧 [DEV] ────────────────────────────────────────────');
    // Deep merge: secretsConfig as base, localConfig overrides
    const mergedConfig = deepMerge(secretsConfig, localConfig);
    console.log('🔧 [DEV] ✅ Config merged successfully!');
    console.log('🔧 [DEV] Base (Secrets Manager) keys:', Object.keys(secretsConfig));
    console.log('🔧 [DEV] Override (Local) keys:', Object.keys(localConfig));
    console.log('🔧 [DEV] Final merged keys:', Object.keys(mergedConfig));
    return mergedConfig;
}
//# sourceMappingURL=local-config-override.util.js.map